import React, { useState } from 'react';
import { Token, decimalsToWei, isEqualHex } from '@akropolis-web/primitives';
import {
  MenuItem as MuiMenuItem,
  ListItemText as MuiListItemText,
  Select as MuiSelect,
  Skeleton,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
} from '@mui/material';
import { combineLatest, map } from 'rxjs';

import { colors, styled } from 'core/styles';
import { FormattedAmount, Loading, SectionWithTitle } from 'components';
import { NetworkSlug, ProductSlug, VersionSlug } from 'domain/types';
import { ArrowDownIcon } from 'components/icons';
import { useSubscribable } from 'utils/react';
import { loadTokenLiqLevelsByMarket } from 'api/modules/apostro-rest/public';
import { getAmount, getMarketName } from 'domain/utils';
import { left } from 'utils/either';
import { protocolsApi } from 'api/modules';
import { schemas } from 'api/modules/apostro-rest/types';

import { LiqLevelData, LiqLevelsChart } from './LiqLevelsChart';
import CheckMarkIcon from './images/CheckMarkIcon.svg';

type PositionsSectionProps = {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  networkSlug: NetworkSlug;
  marketVid: string;
  tokens: Token[];
  defaultToken: Token;
};

export function LiqLevelsSection(props: PositionsSectionProps) {
  const { defaultToken, marketVid, networkSlug, productSlug, tokens, versionSlug } = props;
  const sortedTokens = tokens.sort((a, b) => a.symbol.localeCompare(b.symbol));

  const [currentToken, setCurrentToken] = useState(defaultToken);
  const [liqType, setLiqType] = useState<schemas['LiqType']>('COLLATERAL');

  const handleToggleButtonChange = (_event: React.MouseEvent<HTMLElement>, value: string) => {
    setLiqType(value as schemas['LiqType']);
  };

  const rd = useSubscribable(
    () =>
      combineLatest([
        loadTokenLiqLevelsByMarket({
          product_slug: productSlug,
          version_slug: versionSlug,
          network_slug: networkSlug,
          market_vid: marketVid,
          token_vid: currentToken.address,
          liq_type: 'ALL',
          points_number: 200,
        }),
        protocolsApi.getLendingInfo$(true, { productSlug, versionSlug, network: networkSlug }),
      ]).pipe(
        map(([{ current_price, liq_levels }, lendingInfo]) => {
          const marketName = getMarketName(lendingInfo, marketVid);

          const liqLevels: LiqLevelData[] = liq_levels.map(item => {
            const { from_price: liqPrice, total_liq_volume_usd, type } = item;
            const marketLiqVolume = Math.abs(item.market_liq_volume_usd);
            return {
              type,
              liqPrice,
              marketLiqVolume,
              othersLiqVolume: Math.abs(total_liq_volume_usd) - marketLiqVolume,
            };
          });
          return { liqLevels, marketName, currentPrice: current_price };
        }),
      ),
    [currentToken.address, marketVid, networkSlug, productSlug, versionSlug],
    {
      resetOnChangeTarget: false,
      clientOnly: true,
    },
  ).map(({ liqLevels, ...rest }) => {
    const filteredLevels = liqLevels.filter(({ type }) => type === liqType);
    return {
      ...rest,
      liqLevels: liqType === 'COLLATERAL' ? filteredLevels.reverse() : filteredLevels,
    };
  });

  const priceFormatter = (value: number, zeroIsNA: boolean = false, precision: number = 2) => {
    return (
      <FormattedAmount
        sum={
          value === 0 && zeroIsNA
            ? left('Unknown token price')
            : getAmount(value, '$').mul(decimalsToWei(18))
        }
        precision={precision}
        withSI
      />
    );
  };

  const titleContent = (
    <TitleContentBox>
      <ToggleButtonGroup
        color="primary"
        value={liqType}
        exclusive
        onChange={handleToggleButtonChange}
        aria-label="Platform"
      >
        <ToggleButton value="COLLATERAL">Dump</ToggleButton>
        <ToggleButton value="DEBT">Pump</ToggleButton>
      </ToggleButtonGroup>
      <TokenDataBox>
        <Loading data={rd} loader={null}>
          {data =>
            data.currentPrice !== 0 ? (
              <PriceText>Current price: {priceFormatter(data.currentPrice)}</PriceText>
            ) : null
          }
        </Loading>
        <TokenSelector
          currentToken={currentToken}
          onChange={setCurrentToken}
          tokens={sortedTokens}
        />
      </TokenDataBox>
    </TitleContentBox>
  );

  return (
    <Root>
      <SectionWithTitle title="Liquidation levels" titleContent={titleContent}>
        <Loading data={rd} loader={<Skeleton variant="rounded" height={40} />}>
          {({ liqLevels, marketName }) => {
            return liqLevels.length > 0 ? (
              <LiqLevelsChart
                liqType={liqType}
                liqLevels={liqLevels}
                currentToken={currentToken}
                marketName={marketName}
              />
            ) : (
              <Message>Liquidation levels for {currentToken.symbol} are not available</Message>
            );
          }}
        </Loading>
      </SectionWithTitle>
    </Root>
  );
}

function TokenSelector({
  currentToken,
  onChange,
  tokens,
}: {
  currentToken: Token;
  onChange: (token: Token) => void;
  tokens: Token[];
}) {
  return (
    <Select
      value={currentToken.address}
      size="small"
      IconComponent={SelectIcon}
      MenuProps={menuSX}
      onChange={event =>
        onChange(
          tokens.find(
            token =>
              typeof event.target.value === 'string' &&
              isEqualHex(token.address, event.target.value),
          ) || tokens[0],
        )
      }
    >
      {tokens.map(({ symbol, address }) => (
        <MenuItem key={address} value={address}>
          <TextBox>
            <ListItemText>{symbol}</ListItemText>
          </TextBox>
        </MenuItem>
      ))}
    </Select>
  );
}

const SelectIcon = styled(ArrowDownIcon)(() => ({
  fontSize: '20px',
  '&.MuiSvgIcon-root.MuiSelect-icon': {
    right: 4,
  },
}));

const menuSX = {
  sx: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'transparent',
    },
    '& .MuiMenu-paper': {
      backgroundColor: colors.smokyWhite,
      backgroundImage: 'none',
      borderRadius: '4px',
      marginTop: '4px',
      maxHeight: 180,

      '& .MuiList-root': {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '4px',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.blackOpacity[24],
    },
  },
};

const Select = styled(MuiSelect)(() => ({
  backgroundColor: 'transparent',
  height: 32,

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.blackOpacity[24],
    padding: '0 9px 0 10px',
  },

  '&.Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
    },
  },

  '& .MuiSelect-select.MuiSelect-outlined': {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '4px',
    transition: 'background 300ms',

    '&.MuiInputBase-input': {
      padding: '4px 28px 4px 8px',
    },
  },
}));

const MenuItem = styled(MuiMenuItem)(() => ({
  position: 'relative',
  display: 'block',
  padding: 0,

  '&:not(:last-of-type)': {
    '&::before': {
      position: 'absolute',
      content: '""',
      width: '100%',
      height: '1px',
      bottom: '-4px',
      background: colors.silver,
    },
  },

  '&.MuiMenuItem-root': {
    transition: 'background 300ms',
    minHeight: 'auto',

    '&:hover': {
      backgroundColor: colors.blackOpacity[5],
      borderRadius: '4px',
    },
    '&.Mui-selected': {
      position: 'relative',
      backgroundColor: 'transparent',

      '&::after': {
        position: 'absolute',
        content: `url(${CheckMarkIcon.src})`,
        right: 4,
        top: 6,
        width: 20,
        height: 20,
        background: 'transparent',
      },
    },
    '&.Mui-selected:hover ': {
      backgroundColor: colors.whiteOpacity[5],
    },
  },

  '& .MuiListItemIcon-root': listItemIconStyles,
}));

const listItemIconStyles = {
  minWidth: '16px',
  marginRight: '8px',
  fontSize: 24,
};

const TextBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '7px 10px',

  '.MuiMenuItem-root &': {
    padding: '4px 50px 4px 4px',
  },

  '.MuiSelect-select &': {
    padding: 0,
  },
});

const ListItemText = styled(MuiListItemText)(() => ({
  color: colors.black,
  margin: 0,

  '& .MuiTypography-root': {
    fontSize: '14px',
    lineHeight: 1,
  },
}));

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: 24,
});

const TitleContentBox = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  flexGrow: 1,
  gap: 32,
});

const ToggleButtonGroup = styled(MuiToggleButtonGroup)({
  height: 40,

  '@media (min-width: 425px)': {
    height: 'auto',
  },
});

const ToggleButton = styled(MuiToggleButton)({
  fontSize: 12,
  padding: 6,

  '@media (min-width: 425px)': {
    padding: '4px 8px',
  },
});

const TokenDataBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 16,
});

const PriceText = styled('span')({
  '@media (max-width: 465px)': {
    display: 'grid',
    fontSize: 14,
  },
});

const Message = styled('div')({
  width: '100%',
  padding: '16px 20px',
  background: colors.smokyWhite,
  borderRadius: 16,
  fontSize: 16,
});
